import React, { useEffect, useState } from "react";
import IPage from "interfaces/page";
import { Box, FormControl, InputLabel, Link, MenuItem, Select } from "@mui/material";
import { InstallerTypeFilter } from "pages/dashboard/components/statistics/TopInstallers";
import Spinner from "features/spinner/Spinner";
import { InstallerStatisticFull } from "models/InstallerChecklistStatistics";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  getAllInstallersStatistics,
  selectAllInstallersStatisticsState,
} from "pages/dashboard/statisticsSlice";
import OutlinedCard from "components/OutlinedCard";
import { Link as RouterLink } from "react-router-dom";
import DataTable, { ColumnOptions } from "components/DataTable";
import PageLayout from "components/PageLayout";
import PageTitle from "components/PageTitle";
import { TimePeriod, TimePeriodValues } from "models/UserStatistics";

const columns: ColumnOptions<InstallerStatisticFull> = [
  {
    key: "userName",
    label: "Installatör",
    renderCell: (row) => (
      <Link component={RouterLink} to={`/users/view/${row.userId}`}>
        {row.userName}
      </Link>
    ),
  },
  {
    key: "numberOfCompletedChecklists",
    label: "Slutförda checklistor",
    defaultSortDirection: "desc",
    align: "right",
  },
  {
    key: "numberOfApprovedChecklists",
    label: "Godkända checklistor",
    defaultSortDirection: "desc",
    align: "right",
  },
  {
    key: "percentageApproved",
    label: "Andel godkända (%)",
    defaultSortDirection: "desc",
    align: "right",
    renderCell: (row) => row.percentageApproved.toFixed(1),
  },
];

const InstallerStatistics: React.FC<IPage> = (props) => {
  const dispatch = useAppDispatch();
  const { allInstallerStatistics, isLoading, loaded } = useAppSelector(
    selectAllInstallersStatisticsState
  );

  // Filter values
  const [installerType, setInstallerType] = useState<InstallerTypeFilter>(InstallerTypeFilter.ALL);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("FROM_START");

  useEffect(() => {
    dispatch(
      getAllInstallersStatistics({
        installerType: InstallerTypeFilter[installerType],
        timePeriod: timePeriod,
      })
    );
  }, [installerType, timePeriod]);

  return (
    <PageLayout>
      <PageTitle>Statistik</PageTitle>

      <OutlinedCard>
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <FormControl variant="filled" size="small" sx={{ minWidth: 150, flexGrow: 0 }}>
            <InputLabel>Installatörer</InputLabel>
            <Select
              value={installerType}
              onChange={(ev) => setInstallerType(ev.target.value as InstallerTypeFilter)}
            >
              <MenuItem value={InstallerTypeFilter.ALL}>Alla</MenuItem>
              <MenuItem value={InstallerTypeFilter.INTERNAL_INSTALLER}>Interna</MenuItem>
              <MenuItem value={InstallerTypeFilter.EXTERNAL_INSTALLER}>Externa</MenuItem>
            </Select>
          </FormControl>

          <FormControl variant="filled" size="small" sx={{ minWidth: 200, flexGrow: 0 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
            >
              <MenuItem value="FROM_START">{TimePeriodValues.FROM_START}</MenuItem>
              <MenuItem value="PAST_WEEK">{TimePeriodValues.PAST_WEEK}</MenuItem>
              <MenuItem value="PAST_MONTH">{TimePeriodValues.PAST_MONTH}</MenuItem>
              <MenuItem value="PAST_YEAR">{TimePeriodValues.PAST_YEAR}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isLoading ? (
          <Box py={4} display="grid" sx={{ placeItems: "center" }}>
            <Spinner size="3rem" color="grey" />
          </Box>
        ) : (
          <DataTable
            columns={columns}
            data={allInstallerStatistics}
            getKey={(row) => row.userId}
            striped
          />
        )}
      </OutlinedCard>
    </PageLayout>
  );
};

export default InstallerStatistics;
